import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6tdfmkj8d20juc9020g';
const MAX_CHARS = 500;

const industries = [
  'Hair & Beauty',
  'Builders & Trades',
  'Dental & Clinics',
  'Fitness & Wellness',
  'Physio & Therapy',
  'Home Services',
  'Other',
];

const LeaveReviewForm = () => {
  const [stars, setStars] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (stars === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (review.length > MAX_CHARS) {
      setError(`Review must be ${MAX_CHARS} characters or fewer.`);
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name,
        role,
        industry,
        stars: String(stars),
        review,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-ea-dark-card border border-ea-blue/30 rounded-2xl p-10 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/30 mx-auto mb-5">
          <i className="ri-check-line text-ea-blue text-2xl"></i>
        </div>
        <h3 className="text-white font-bold text-xl mb-2">Thank you for your review!</h3>
        <p className="text-ea-gray text-sm">We really appreciate you taking the time to share your experience.</p>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      {/* Left - copy */}
      <div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
          <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Leave a Review</span>
        </div>
        <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
          Had a great experience?<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
            Tell others about it.
          </span>
        </h3>
        <p className="text-ea-gray text-sm leading-relaxed max-w-sm">
          Your feedback helps other business owners understand what&apos;s possible. It takes less than a minute and makes a real difference to businesses like yours doing their research.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          {[
            { icon: 'ri-shield-check-line', text: 'Your review goes straight to our team' },
            { icon: 'ri-time-line', text: 'Takes about 60 seconds' },
            { icon: 'ri-heart-line', text: 'Genuinely appreciated by the whole team' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-ea-blue/10 border border-ea-blue/20 flex-shrink-0">
                <i className={`${item.icon} text-ea-blue`} style={{ fontSize: '13px' }}></i>
              </div>
              <p className="text-ea-gray text-sm">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right - form */}
      <form
        data-readdy-form
        onSubmit={handleSubmit}
        className="bg-ea-dark-card border border-ea-dark-border rounded-2xl p-8 flex flex-col gap-5"
      >
        {/* Star rating */}
        <div>
          <label className="block text-white text-sm font-medium mb-3">Your rating</label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setStars(n)}
                onMouseEnter={() => setHoverStar(n)}
                onMouseLeave={() => setHoverStar(0)}
                className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform duration-100 hover:scale-110"
              >
                <i
                  className={`ri-star-${n <= (hoverStar || stars) ? 'fill' : 'line'} text-2xl transition-colors duration-150`}
                  style={{ color: n <= (hoverStar || stars) ? '#00c2ff' : '#4a5568' }}
                ></i>
              </button>
            ))}
            {stars > 0 && (
              <span className="text-ea-gray text-xs ml-1">
                {['', 'Poor', 'Below average', 'Average', 'Good', 'Excellent'][stars]}
              </span>
            )}
          </div>
        </div>

        {/* Name + Role row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wide">
              Your name
            </label>
            <input
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sarah T."
              className="w-full bg-ea-navy border border-ea-dark-border rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-ea-gray/40 focus:outline-none focus:border-ea-blue/50 transition-colors"
            />
          </div>
          <div>
            <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wide">
              Your role
            </label>
            <input
              name="role"
              type="text"
              required
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Salon Owner"
              className="w-full bg-ea-navy border border-ea-dark-border rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-ea-gray/40 focus:outline-none focus:border-ea-blue/50 transition-colors"
            />
          </div>
        </div>

        {/* Industry */}
        <div>
          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wide">
            Industry
          </label>
          <select
            name="industry"
            required
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full bg-ea-navy border border-ea-dark-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-ea-blue/50 transition-colors cursor-pointer appearance-none"
            style={{ color: industry ? 'white' : '#6b7280' }}
          >
            <option value="" disabled>Select your industry</option>
            {industries.map((ind) => (
              <option key={ind} value={ind} style={{ color: 'white', background: '#0d1117' }}>
                {ind}
              </option>
            ))}
          </select>
        </div>

        {/* Review text */}
        <div>
          <label className="block text-ea-gray text-xs font-medium mb-1.5 uppercase tracking-wide">
            Your review
          </label>
          <textarea
            name="review"
            required
            value={review}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setReview(e.target.value);
            }}
            rows={4}
            placeholder="What changed for your business? What result stood out most?"
            className="w-full bg-ea-navy border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm placeholder:text-ea-gray/40 focus:outline-none focus:border-ea-blue/50 transition-colors resize-none leading-relaxed"
          />
          <div className="flex justify-between mt-1">
            <span className="text-ea-gray/50 text-xs">Be as specific as possible - it helps others the most</span>
            <span className={`text-xs ${review.length >= MAX_CHARS - 50 ? 'text-amber-400' : 'text-ea-gray/50'}`}>
              {review.length}/{MAX_CHARS}
            </span>
          </div>
        </div>

        {error && (
          <p className="text-red-400 text-xs flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-lg bg-ea-blue text-white font-semibold text-sm hover:bg-ea-blue-electric transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </span>
          ) : (
            'Submit Review'
          )}
        </button>
      </form>
    </div>
  );
};

export default LeaveReviewForm;
